import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';
import type { GeneralMessage } from '../../apps/types';

export type ServiceId = string;
export type ActionId = string;
export type ActionCallId = string;

export type AvailableInfo = {
  android?: string;
  windows?: string; // native has a typo
  window?: string;
  ios?: string;
  mac?: string;
  linux?: string;
};

export type Action = {
  description: string;
  id: string;
  isAsynchronous: boolean;
  name: string;
  optionalParams?: string[];
  requiredParams?: string[];
  availableInfo: null | AvailableInfo;
};

export type EmptyActionData = {};

export type ActionWithServiceId = { serviceId: ServiceId } & Action;

export type SkillDescriptor = {
  description: string;
  name: string;
  parameters: {
    [key: string]: string;
  };
  return: {
    [key: string]: string;
  };
};

export type SkillDescriptors = SkillDescriptor[];

// Service related types
export interface ServiceHubService {
  actions: Action[];
  serviceId: string;
  serviceName: string;
  skillDescriptors: SkillDescriptor[];
}

export type ServiceHubServices = ServiceHubService[];

export type ServiceHubAllServices = {
  services: ServiceHubServices;
};

export enum ActionEventType {
  ActionCompleted,
  ActionFailed,
  ServiceRegistered,
  ServiceUnregistered,
  ServiceDisabled,
  ServiceEnabled,
  CustomNotiEvent,
  ActionAsyncWait,
  ActionStreamResponse,
  ActionDataChanged,
}

export type StreamDataHandler<T> = (data: T) => void;
export type StreamCloseHandler = () => void;
export type StreamErrorHandler = (error: Error) => void;

export interface ActionStream<T> {
  onData: (handler: StreamDataHandler<T>) => void;
  onClose: (handler: StreamCloseHandler) => void;
  onError: (handler: StreamErrorHandler) => void;
  close: () => void;
}

export type ServiceHubEventData<R = any> = {
  actionId: ActionId;
  eventData: R;
  notification: 'ZoomServiceHubEvent';
  serviceId: ServiceId;
  timestamp: number;
  type: ActionEventType;
  actionCallId: string;
};

export type JSServiceInvocationData<R = unknown> = {
  serviceId: string;
  actionId: string;
  actionCallId: string;
  data?: R;
  notification: 'zmOnServiceHubJSAction';
};

export type ServiceHubAddListener = NativeAPIResponseMessage<GeneralMessage>;
export type ServiceHubQueryAllSkills =
  NativeAPIResponseMessage<SkillDescriptors>;
export type ServiceHubExecuteAction = NativeAPIResponseMessage<GeneralMessage>;

// Action request and response types
export type ServiceHubAction<T> = {
  actionId: string;
  actionCallId: string;
  reportEvent?: boolean;
  service: string;
  data?: T;
};

export type GeneralActionResponse = {
  success: boolean;
  message?: string;
};
export type ActionRequest<T> = Omit<ServiceHubAction<T>, 'actionCallId'>;
export type ActionResponse<R = GeneralActionResponse> = ServiceHubEventData<R>;

export class ServiceHubError extends Error {
  constructor(
    public readonly serviceId: ServiceId,
    public readonly actionId: ActionId,
    message: string
  ) {
    super(
      message ||
        `[@zoom/hybrid-jssdk] service hub call ${serviceId}/${actionId} error.`
    );
    this.name = 'ServiceHubError';
  }
}

export type CallActionOption = {
  reportEvent?: boolean;
};
